import { insertIf } from '@fastre/core/helperFunctions/array'
import { capitalize } from '@fastre/core/helperFunctions/string'
import { InternalSaleListingSchema, listingEnabledForUser } from '@fastre/core/schemas/saleListing'
import { DeleteRounded, EditRounded, MoreVertRounded, SaveRounded } from '@mui/icons-material'
import {
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Dropdown,
    Menu,
    MenuButton,
    MenuItem,
    Modal,
    ModalClose,
    ModalDialog,
    Option,
    Select,
    Tab,
    Typography,
    useTheme,
} from '@mui/joy'
import { useApi } from 'api'
import {
    ListingFilesProvider,
    ListingLedgerProvider,
    useListingsApi,
    useListingType,
    useUsersApi,
} from 'apiProviders'
import { useUserData } from 'auth'
import Loading from 'components/Loading'
import { dontCloseOnBackgroundClick } from 'components/modal'
import { ScrollableTabList, ScrollableTabs } from 'components/scrollableTabs'
import SectionHead from 'components/sectionHead'
import { useOrgId } from 'helpers'
import ListingLedger from 'ledgerManager/listingLedger'
import { pick, pipe, prop } from 'ramda'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, Route, Routes, useMatch, useNavigate, useParams } from 'react-router'
import AuditLog from './auditLog'
import ConjunctionalAgreements from './conjunctionalAgreements'
import Contract from './contract'
import VoidContractModal from './contract/voidContractModal'
import CoreListingDetails from './coreListingDetails'
import { EditModeProvider, useEditMode } from './editMode'
import Files from './files'
import TrustFundTab from './fundsInTrust/trustFundTab'
import GenericDocumentStepper from './genericDocumentStepper'
import { listingStatusToColor, useListingStatusOptions } from './helpers'
import { ListingContext, useListingContext } from './listingProvider'
import MarketingTab from './marketingTab'
import DisclosureTab from './sellerDisclosureTab'
import tenantInfo from './tenantInfo'

const SaleListingDetails = forwardRef(({}, ref) => (
    <CoreListingDetails
        type="sale"
        ref={ref}
    />
))
const BdmListingDetails = () => <CoreListingDetails type="bdm" />

const ListingStatusChip = () => {
    const api = useApi()
    const listingType = useListingType()
    const { listing, setListing } = useListingContext()
    const listingStatusOptions = useListingStatusOptions(listing)
    const listingsApi = useListingsApi()

    const [loading, setLoading] = useState(false)

    if (loading) {
        return (
            <CircularProgress
                size="sm"
                sx={{ '--CircularProgress-size': '24px' }}
            />
        )
    }

    return (
        <Box>
            <Dropdown>
                <MenuButton
                    slots={{ root: Chip }}
                    slotProps={{
                        root: {
                            variant: 'soft',
                            color: listingStatusToColor(listing.listingStatus),
                            size: 'lg',
                            onClick: e => e.stopPropagation(),
                        },
                    }}
                >
                    {/* {listing.listingStatus.split(' ').map(capitalize).join(' ')*/}
                    {listing.listingStatus.split(' ').map(capitalize).join(' ')}
                </MenuButton>
                <Menu onClick={e => e.stopPropagation()}>
                    {listingStatusOptions.map(({ value, disabled }) => (
                        <MenuItem
                            key={value}
                            disabled={disabled}
                            onClick={async () => {
                                setLoading(true)
                                try {
                                    const updatedListing = (await api
                                        .post(`/listing/${listingType}/${listing.listingId}/status`, {
                                            listingStatus: value,
                                        })
                                        .then(prop('data'))) as InternalSaleListingSchema

                                    setListing({
                                        ...listing,
                                        ...pick(['listingStatus', 'balanceSheet'], updatedListing),
                                    })
                                    setListing({ ...listing, listingStatus: value })
                                    listingsApi.refreshSpecific(listing.listingId)
                                } catch (e) {
                                    console.error(e)
                                } finally {
                                    setLoading(false)
                                }
                            }}
                        >
                            {capitalize(value)}
                        </MenuItem>
                    ))}
                </Menu>
            </Dropdown>
        </Box>
    )
}

const Inner = () => {
    const type = useListingType()
    const api = useApi()
    const navigate = useNavigate()
    const { listing } = useListingContext()
    const match = useMatch(`:orgId/listings/${type}/:listingId/:tab`)
    const theme = useTheme()
    const { user } = useUserData()
    const { listingId } = useParams<{ listingId: string }>()
    const formRef = useRef<any>(null)
    const usersApi = useUsersApi()
    const orgId = useOrgId()
    const { editMode, setEditMode } = useEditMode()

    const isNew = listingId == 'new'

    const tabs = useMemo(
        () =>
            [
                ['Listing', type == 'sale' ? SaleListingDetails : BdmListingDetails],
                ['Marketing', MarketingTab],
                ...insertIf(type == 'sale', ['Conjunctional Agreements', ConjunctionalAgreements]),
                ...insertIf(listing.occupantStatus == 'investment', ['Tenancy Info', tenantInfo]),
                ['Seller Disclosure', DisclosureTab],
                ...insertIf(type == 'sale', ['Contract', Contract]),
                ...insertIf(type == 'sale' && user.permissions.includes('fit.view'), [
                    'Funds in Trust',
                    TrustFundTab,
                ]),
                ['Files', Files],
                ['eSign Documents', () => <GenericDocumentStepper title="Generic Documents" />],
                ['Ledger', ListingLedger],
                ['Audit Log', AuditLog],
            ] as [string, () => React.ReactNode][],
        [listing?.propertyType, type, listing?.occupantStatus, usersApi.loading],
    )

    const [showDeleteListing, setShowDeleteListing] = useState(false)
    const [voidModalOpen, setVoidModalOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const activeTab = match?.params.tab

    const enabledForUser = listingEnabledForUser(listing, usersApi.data, user)
    const formFullyDisabled =
        !isNew &&
        !user.permissions.includes('listings.fullControl') &&
        !(enabledForUser && user.permissions.includes('listings.edit'))

    // Scroll to active tab
    const tabListRef = useRef<HTMLDivElement>(null)
    const activeTabIndex = tabs.findIndex(([label]) => label.toLowerCase().replaceAll(' ', '-') == activeTab)
    useEffect(() => {
        if (!tabListRef.current || activeTabIndex < 0) return
        const tab = tabListRef.current.querySelector(`[role="tab"]:nth-child(${activeTabIndex + 1})`)
        if (!tab) return
        const tabListRect = tabListRef.current.getBoundingClientRect()
        const tabRect = (tab as HTMLElement).getBoundingClientRect()
        const offset = tabRect.left - tabListRect.left + tabListRef.current.scrollLeft
        const center = offset - tabListRect.width / 2 + tabRect.width / 2
        tabListRef.current.scrollTo({ left: center, behavior: 'smooth' })
    }, [activeTab])

    const [menuOpen, setMenuOpen] = useState(false)
    const anchorRef = useRef<any>(null)

    const handleMenuToggle = () => {
        setMenuOpen(prevOpen => !prevOpen)
    }

    const handleMenuClose = () => {
        setMenuOpen(false)
    }

    // Handle clicks outside the menu
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const menuElement = document.getElementById('listing-actions-menu')
            if (
                menuOpen &&
                anchorRef.current &&
                !anchorRef.current.contains(event.target as Node) &&
                menuElement &&
                !menuElement.contains(event.target as Node)
            ) {
                handleMenuClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [menuOpen])

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box>
                    <Box
                        sx={{
                            px: {
                                xs: 2,
                                sm: 4,
                            },
                        }}
                    >
                        <SectionHead
                            title={
                                isNew
                                    ? 'New Listing'
                                    : listing.listingAddress?.streetName
                                      ? `${(listing.listingAddress.unitNumber?.length ?? 0) > 0 ? listing.listingAddress.unitNumber + '/' : ''}${listing.listingAddress?.streetNumber} ${listing.listingAddress?.streetName}`
                                      : 'no street name'
                            }
                            titleChip={!isNew && <ListingStatusChip />}
                            breadcrumbs={[`${type == 'bdm' ? 'BDM ' : ''}Listings`]}
                            buttons={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1, // Adjust spacing between buttons
                                        alignItems: 'center', // Vertical alignment
                                    }}
                                >
                                    {!formFullyDisabled && (
                                        <ButtonGroup>
                                            {editMode ? (
                                                [
                                                    <Button
                                                        key="save"
                                                        variant="solid"
                                                        color="primary"
                                                        startDecorator={<SaveRounded />}
                                                        loading={saving}
                                                        //disabled={isNew && !listing.listingAddress}
                                                        onClick={async () => {
                                                            console.log('formRef', formRef.current)
                                                            setSaving(true)
                                                            const pass = await formRef.current?.saveForm()
                                                            if (pass) {
                                                                setEditMode(false)
                                                            }
                                                            setSaving(false)
                                                        }}
                                                    >
                                                        Save
                                                    </Button>,
                                                    <Button
                                                        key="cancel"
                                                        variant="outlined"
                                                        loading={saving}
                                                        onClick={() => {
                                                            if (isNew) {
                                                                navigate(`/${orgId}/listings/sale`, {
                                                                    replace: true,
                                                                })
                                                            }
                                                            formRef.current?.resetForm()
                                                            setEditMode(false)
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>,
                                                ]
                                            ) : (
                                                <Button
                                                    onClick={() => setEditMode(true)}
                                                    variant="solid"
                                                    color="primary"
                                                    startDecorator={<EditRounded />}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                            {user.permissions.includes('listings.delete') && (
                                                <Button
                                                    ref={anchorRef}
                                                    aria-controls={
                                                        menuOpen ? 'listing-actions-menu' : undefined
                                                    }
                                                    aria-expanded={menuOpen ? 'true' : undefined}
                                                    aria-haspopup="menu"
                                                    onClick={handleMenuToggle}
                                                >
                                                    <MoreVertRounded fontSize="large" />
                                                </Button>
                                            )}
                                        </ButtonGroup>
                                    )}
                                </Box>
                            }
                        />
                    </Box>
                    <Box
                        sx={{
                            [theme.breakpoints.down('sm')]: {
                                display: 'none',
                            },
                        }}
                    >
                        <ScrollableTabs
                            value={tabs.findIndex(
                                ([label]) => label.toLowerCase().replaceAll(' ', '-') == activeTab,
                            )}
                            onChange={(event, val) => {
                                if (val != undefined) {
                                    navigate(`./${tabs[val][0].toLowerCase().replaceAll(' ', '-')}`, {
                                        replace: true,
                                        relative: 'path',
                                    })
                                }
                            }}
                        >
                            <ScrollableTabList ref={tabListRef}>
                                {tabs
                                    .filter((_, i) => !isNew || i <= 0)
                                    .map(([label], i) => (
                                        <Tab
                                            key={label}
                                            value={i}
                                        >
                                            {label}
                                        </Tab>
                                    ))}
                            </ScrollableTabList>
                        </ScrollableTabs>
                    </Box>
                    <Box
                        sx={{
                            [theme.breakpoints.up('sm')]: {
                                display: 'none',
                            },
                        }}
                    >
                        <Select
                            value={activeTab}
                            onChange={(e, val) => {
                                if (val) {
                                    navigate(`./${val}`, {
                                        replace: true,
                                        relative: 'path',
                                    })
                                }
                            }}
                            sx={{
                                mx: 2,
                            }}
                        >
                            {tabs
                                .filter(([val], i) => !isNew || i == 0)
                                .map(([label], i) => (
                                    <Option
                                        key={label}
                                        value={label.toLowerCase().replaceAll(' ', '-')}
                                    >
                                        {label}
                                    </Option>
                                ))}
                        </Select>
                        <Divider sx={{ mt: 2 }} />
                    </Box>
                </Box>
                <Box
                    sx={{
                        px: {
                            xs: 2,
                            sm: 4,
                        },
                        mt: 4,
                        flex: '1 1 auto',
                        overflow: 'auto',
                    }}
                >
                    <Routes>
                        {(isNew ? ([tabs[0]] as any) : tabs).map(([label, Component], i) => (
                            <Route
                                key={label}
                                path={label.toLowerCase().replaceAll(' ', '-')}
                                element={<Component ref={formRef} />}
                            />
                        ))}
                        <Route
                            index
                            element={
                                <Navigate
                                    to={tabs[0][0].toLowerCase().replaceAll(' ', '-')}
                                    replace={true}
                                />
                            }
                        />
                    </Routes>
                </Box>
            </Box>
            <Modal
                open={showDeleteListing}
                onClose={dontCloseOnBackgroundClick(() => setShowDeleteListing(false))}
            >
                <ModalDialog>
                    <ModalClose />
                    <DialogTitle>Delete Listing</DialogTitle>
                    <DialogContent>
                        <Typography>Are you sure you want to delete this listing?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="danger"
                            loading={loading}
                            onClick={async () => {
                                setLoading(true)
                                await api.delete(`/listing/${type}/${listingId}`)
                                setLoading(false)
                                navigate(`../`, { replace: true })
                            }}
                            startDecorator={<DeleteRounded />}
                        >
                            Delete
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setShowDeleteListing(false)}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
            <VoidContractModal
                open={voidModalOpen}
                onClose={voided => {
                    setVoidModalOpen(false)
                    if (voided) {
                        window.location.reload()
                    }
                }}
            />
            <Menu
                id="listing-actions-menu"
                anchorEl={anchorRef.current}
                open={menuOpen}
                onClose={handleMenuClose}
                disablePortal
                placement="bottom-end"
                sx={{
                    zIndex: 1205,
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleMenuClose()
                        setShowDeleteListing(true)
                    }}
                >
                    Delete Listing
                </MenuItem>
                {user.permissions.includes('listings.fullControl') && activeTab == 'contract' && (
                    <MenuItem
                        onClick={() => {
                            handleMenuClose()
                            setVoidModalOpen(true)
                        }}
                    >
                        Void Contract
                    </MenuItem>
                )}
            </Menu>
        </>
    )
}

export default () => {
    const api = useApi()
    const { user } = useUserData()
    const usersApi = useUsersApi()
    const { listingId } = useParams<{ listingId: string }>()
    const [listing, setListing] = useState<InternalSaleListingSchema | undefined>(undefined)
    const type = useListingType()

    const isNew = listingId == 'new'

    useEffect(() => {
        if (listingId == 'new') {
            if (user.salesAssistantConjunctionalAgencyId) {
                usersApi.promiseData.then(users => {
                    const conjUser = users
                        .filter(x => x.userId != user.userId)
                        .find(x => x.conjunctionalAgencyId == user.salesAssistantConjunctionalAgencyId)

                    setListing({
                        listingStatus: 'offline',
                        propertyType: 'residential',
                        agents: [
                            {
                                userId: conjUser?.userId ?? user.userId,
                                split: 1,
                            },
                        ],
                    } as any)
                })
            } else {
                setListing({
                    listingStatus: 'offline',
                    propertyType: 'residential',
                    agents: [
                        {
                            userId: user.salesAssistantLeadAgentId ?? user.userId,
                            split: 1,
                        },
                    ],
                } as any)
            }
        } else {
            api.get(`/listing/${type}/${listingId}`).then(pipe(prop('data'), setListing))
        }
    }, [listingId])

    const refreshListing = () => api.get(`/listing/${type}/${listingId}`).then(pipe(prop('data'), setListing))

    if (!listing) {
        return <Loading />
    }

    return (
        <ListingContext.Provider value={{ listing, setListing: setListing as any, refreshListing }}>
            <EditModeProvider defaultEdit={isNew}>
                <ListingFilesProvider>
                    <ListingLedgerProvider>
                        <Inner />
                    </ListingLedgerProvider>
                </ListingFilesProvider>
            </EditModeProvider>
        </ListingContext.Provider>
    )
}
